/**
 * Which BEST this is, in the corner of every screen - issue #83.
 *
 * "2026.10.1 · 594fc2f1": the release, and the first eight characters of the
 * commit it was built from. When somebody says "it does that on the live one",
 * this is the answer to "which one is live" without asking anybody.
 *
 * `dev` where the commit would be, when the server does not know it: a
 * developer's run outside a git checkout, or an image built without the deploy
 * job's record. The version is still shown - it comes from the source, not
 * from the deploy.
 *
 * MOUNTED ONCE, in App, and not inside the Layout: the sign-in screen and the
 * program picker are screens too, and they are exactly where somebody is
 * stuck and wants to say which build they are looking at. /api/version needs
 * no sign-in for the same reason.
 *
 * Fixed at the bottom-left rather than inside the sidebar, which is closed on
 * a phone and when the person has hidden it - a label that vanishes with the
 * drawer is not on every screen. The two places it could cover are given room
 * where they are: Layout pads the content and the sidebar's scroll area at the
 * bottom by more than this is tall.
 *
 * It draws NOTHING until the request succeeds, and nothing if it fails. A
 * version label is the least important thing on the page; it must never be the
 * thing that throws, or the reason a blank error appears.
 */

import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/material/styles'
import { api } from '../../api'
import type { VersionInfo } from '../../schema'
import { MONOSPACE } from '../../theme'
import { buildLabelText } from './versionLabel'

export default function BuildLabel() {
  const theme = useTheme()
  const [info, setInfo] = useState<VersionInfo | null>(null)

  useEffect(() => {
    let live = true
    api
      .version()
      .then((answer) => {
        if (live) setInfo(answer)
      })
      .catch(() => {
        // Show nothing. See the header.
      })
    return () => {
      live = false
    }
  }, [])

  if (!info) return null

  const built = info.built_at ? new Date(info.built_at) : null
  const builtText = built && !Number.isNaN(built.getTime()) ? built.toLocaleString() : info.built_at

  return (
    <Tooltip
      placement="top-start"
      title={
        <>
          <div>
            {info.commit_full
              ? `Commit ${info.commit_full}`
              : 'No commit recorded: this is not a deployed build.'}
          </div>
          {builtText && <div>Built {builtText}</div>}
        </>
      }
    >
      <Box
        component="span"
        data-testid="build-label"
        sx={{
          position: 'fixed',
          left: theme.spacing(1.5),
          bottom: theme.spacing(0.5),
          // Over the drawer (which the app bar sits above) and under every
          // dialog and menu, so nothing that opens is ever behind it.
          zIndex: theme.zIndex.drawer + 2,
          fontFamily: MONOSPACE,
          fontSize: '0.6875rem',
          lineHeight: 1.6,
          color: 'text.secondary',
          opacity: 0.8,
          // It takes the pointer, because the tooltip is a hover. That is safe
          // only because of the clearance above: nothing clickable is ever
          // laid out under it.
          userSelect: 'text',
        }}
      >
        {buildLabelText(info)}
      </Box>
    </Tooltip>
  )
}
